import { Button } from 'antd';
import Modal from '../../../components/Modal';
import { AlimtalkPreviewState } from '../../../types/alimtalk/form';
import { renderAlimtalkText } from './KakaoPreview';
import * as Styled from './style';

export type PreviewConfirmModalProps = {
  /** null이면 닫힘 */
  previewState: AlimtalkPreviewState | null;
  /** 운영자 선택 변수 값 — ACTION 모드의 백엔드 preview 메시지는 원문이라 클라에서 치환 */
  previewVariables: Record<string, string>;
  isRetrying: boolean;
  isRegistering: boolean;
  onRetry: () => void;
  onConfirm: () => void;
  onClose: () => void;
};

/**
 * 등록 전 preview(dry-run) 확인 모달 — 백엔드 권장 플로우(preview 확인 후 등록).
 * preview 실패 시에도 등록을 막지 않는다(읽기 전용 dry-run의 transient 장애가 발송 업무를 블로킹하지 않도록)
 * — 실패 사유 + [다시 시도]를 보여주고 확인 버튼은 "그대로 등록"으로 동작.
 */
export const PreviewConfirmModal = (props: PreviewConfirmModalProps) => {
  const { previewState, previewVariables, isRetrying, isRegistering, onRetry, onConfirm, onClose } = props;
  const isPreviewFailed = previewState != null && previewState.result == null;
  // EXPIRE만 csvS3Key 없이 등록(#4841) — 대상이 CSV가 아니라 쿠폰 보유자 전원(rawCount=dedupedCount=보유자 수)
  const isHolderTarget = previewState != null && previewState.dto.csvS3Key == null;

  return (
    <Modal
      isOpen={previewState != null}
      title="발송 등록 확인"
      width={520}
      isFooter
      okText={isRegistering ? '등록 중…' : isPreviewFailed ? '미리보기 없이 등록' : '등록'}
      onOk={() => {
        if (!isRegistering) {
          onConfirm();
        }
      }}
      // 등록 진행 중 닫기(X·닫기 버튼·마스크 클릭) 차단 — 모달이 닫히면 previewState가 비워져
      // "등록 중…" 진행 상태를 확인할 방법이 사라진다
      maskClosable={!isRegistering}
      onClose={() => {
        if (!isRegistering) {
          onClose();
        }
      }}
      destroyOnClose
    >
      {previewState?.result && (
        <>
          <Styled.FieldRow>
            <Styled.FieldLabel>발송 대상</Styled.FieldLabel>
            <Styled.PreviewStatRow>
              {isHolderTarget ? (
                <Styled.PreviewStat>
                  <Styled.PreviewStatLabel>쿠폰 보유자 (미사용·미만료)</Styled.PreviewStatLabel>
                  <Styled.PreviewStatValue>
                    {previewState.result.dedupedCount.toLocaleString()}명
                  </Styled.PreviewStatValue>
                </Styled.PreviewStat>
              ) : (
                <>
                  <Styled.PreviewStat>
                    <Styled.PreviewStatLabel>CSV 행 수</Styled.PreviewStatLabel>
                    <Styled.PreviewStatValue>
                      {previewState.result.rawCount.toLocaleString()}행
                    </Styled.PreviewStatValue>
                  </Styled.PreviewStat>
                  <Styled.PreviewStat>
                    <Styled.PreviewStatLabel>중복 제거 후 대상</Styled.PreviewStatLabel>
                    <Styled.PreviewStatValue>
                      {previewState.result.dedupedCount.toLocaleString()}명
                    </Styled.PreviewStatValue>
                  </Styled.PreviewStat>
                </>
              )}
            </Styled.PreviewStatRow>
          </Styled.FieldRow>

          {previewState.result.message && (
            <Styled.FieldRow>
              <Styled.FieldLabel>1행 메시지 미리보기</Styled.FieldLabel>
              {/* 백엔드가 붙이는 [DRY-RUN] 개발용 prefix는 운영자에게 의미 없어 제거.
                  ACTION 모드만 클라 치환 — CSV 모드(dto와 함께 동결된 판별값)는 백엔드 치환 결과 그대로 노출해
                  미치환 {{변수}}(헤더 오탈자 등)를 검수 가능하게 */}
              <Styled.PreviewMessageBox>
                {(() => {
                  const message = previewState.result.message.replace(/^\[DRY-RUN\]\s*/, '');
                  return previewState.isCsvMode ? message : renderAlimtalkText(message, previewVariables);
                })()}
              </Styled.PreviewMessageBox>
            </Styled.FieldRow>
          )}
        </>
      )}

      {isPreviewFailed && (
        <Styled.FieldRow>
          <Styled.FieldLabel>발송 미리보기 실패</Styled.FieldLabel>
          <Styled.PreviewErrorBox>
            {previewState.errorMessage}
            <Button size="small" loading={isRetrying} onClick={onRetry}>
              다시 시도
            </Button>
          </Styled.PreviewErrorBox>
          <Styled.Hint>미리보기 없이도 등록은 가능합니다. 대상 수 확인이 필요하면 다시 시도해주세요.</Styled.Hint>
        </Styled.FieldRow>
      )}
    </Modal>
  );
};
